// @flow
import * as React from 'react';
import { Formik, Form } from 'formik';
import type { FormikActions } from 'formik';
import * as Yup from 'yup';
import { injectIntl, FormattedMessage } from 'react-intl';

// Components
import { Button } from 'components/atoms';
import {
  TextField,
  TextFieldWithIcon,
  PhoneTextField,
  FormikField
} from 'components/api/';

// Utils
import { isObjEmpty } from 'utils/varValidation';
import { isPhoneNumberValid } from 'utils/string';

import './AdditionalInfoForm.scss';

export type Profile = {|
  /**
   * First name, the provider always provides this field
   */
  firstName: string,
  /**
   * First name, the provider always provides this field
   */
  lastName: string,
  /**
   * Phone number
   */
  phoneNumber?: string,
  /**
   * Email, available most of the time (facebook doesn't require an email
   * to create an account)
   */
  email?: string
|};

type Props = {
  /**
   * The info retrieved from the provider
   */
  profile: Profile,
  /**
   * Needed to validate the phone number
   */
  countryCode: string,
  /**
   * Callback invoked when the uses clicks on the "continue" button
   */
  onSubmit: (profile: Profile, form: FormikActions<Profile>) => void,
  intl: Object
};

const additionalInfoSchema = (props: Props) => {
  return Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    phoneNumber: Yup.string()
      .required()
      .test({
        name: 'phoneNumber',
        message: 'Phone number is not valid',
        test(value?: string = '') {
          return isPhoneNumberValid(value, props.countryCode);
        }
      }),
    email: Yup.string()
      .email()
      .required()
  });
};

function Header({ profile }: {| profile: Profile |}) {
  if (!profile.phoneNumber) {
    return (
      <FormattedMessage id="c8.additional.info.form.we.need.your.phone.number.to.continue">
        {msg => msg}
      </FormattedMessage>
    );
  }
  if (!profile.email) {
    return (
      <FormattedMessage id="c8.additional.info.form.we.need.your.email.address.to.continue">
        {msg => msg}
      </FormattedMessage>
    );
  }
}

const AdditionalInfoForm = (props: Props) => {
  const { intl } = props;
  function onSubmit(profile: Profile, form: FormikActions<Profile>) {
    // remove unwanted characters from phone number before reporting the output
    if (profile.phoneNumber) {
      profile.phoneNumber = profile.phoneNumber.replace(/[^\d]/g, '');
    }
    props.onSubmit(profile, form);
  }

  return (
    <Formik
      initialValues={props.profile}
      validationSchema={additionalInfoSchema(props)}
      onSubmit={onSubmit}
      render={({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty
      }) => {
        const ns = AdditionalInfoForm.block;
        const disabled = !dirty || !isObjEmpty(errors);
        return (
          <Form className={ns} onSubmit={handleSubmit}>
            <div className={`${ns}__title`}>
              <Header profile={props.profile} />
            </div>
            <FormikField
              renderedComponent={TextField}
              name="firstName"
              disabled={true}
              placeholder="First Name"
              aria-label={intl.formatMessage({
                id: 'c8.aria.additional.info.first.name',
                defaultMessage: 'Enter first name for login'
              })}
            />
            <FormikField
              renderedComponent={TextField}
              name="lastName"
              disabled={true}
              placeholder="Last Name"
              aria-label={intl.formatMessage({
                id: 'c8.aria.additional.info.last.name',
                defaultMessage: 'Enter last name for login'
              })}
            />
            <FormikField
              renderedComponent={TextFieldWithIcon}
              component={PhoneTextField}
              countryCode={props.countryCode}
              name="phoneNumber"
              type="tel"
              icon="phone"
              disabled={'phoneNumber' in props.profile}
              placeholder="Phone Number"
              aria-label={intl.formatMessage({
                id: 'c8.aria.additional.info.phone.number',
                defaultMessage: 'Enter phone number for login'
              })}
            />
            <FormikField
              renderedComponent={TextFieldWithIcon}
              icon="email"
              name="email"
              type="email"
              disabled={'email' in props.profile}
              placeholder={intl.formatMessage({ id: 'c8.common.email' })}
              aria-label={intl.formatMessage({
                id: 'c8.aria.additional.info.email',
                defaultMessage: 'Enter email for login'
              })}
            />
            <Button
              type="Submit"
              size="medium"
              disabled={disabled}
              aria-label={intl.formatMessage({
                id: 'c8.aria.additional.info.submit',
                defaultMessage: 'Update user info and login'
              })}
            >
              {intl.formatMessage({
                id: 'c8.common.continue'
              })}
            </Button>
          </Form>
        );
      }}
    />
  );
};

AdditionalInfoForm.block = 'additional-info-form';
export default injectIntl(AdditionalInfoForm);



// WEBPACK FOOTER //
// ./src/components/pages/User/Social/AdditionalInfo/AdditionalInfoForm.js